import React, { useEffect, useContext, useState, useCallback } from "react"
import PropTypes from "prop-types"
import { AppContext } from "../components/context/AppContext"
import { OfferApi } from "../utils/api"
import { MaintenanceModeComponent, PageNotFound, CheckStatusComponent } from "../components"
import { SamsClubGetStartedComponent, SamsClubWelcomeComponent } from "../components/SamsClub"
import AnalyticsService from "../utils/AnalyticsService"
import { ROUTE_PATHS } from "../utils/routes"
import { PROMO_CUSTOM_LANDING_PAGE_VIEW } from "../utils/GA4Events"

export const SamsClub = () => {
  const { dispatch, state } = useContext(AppContext)
  const [inMaintenanceMode, setInMaintenanceMode] = useState(true)
  const [maintenanceText, setMaintenanceText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isErrorFetchingMaintenanceText, setIsErrorFetchingMaintenanceText] = useState(false)
  const [landingPageImageText, setLandingPageImageText] = useState("")
  const [landingPageImage, setLandingPageImage] = useState("")
  const [landingPageFirstSection, setLandingPageFirstSection] = useState("")
  const [landingPageSecSection, setLandingPageSecSection] = useState("")
  const [campaignId, setCampaignId] = useState(0)
  const [offerEpire, setOfferExpire] = useState("")
  const [isPageNotFound, setIsPageNotFound] = useState(false)

  const offerId = Number.parseInt(process.env.REACT_APP_SAMSCLUB_OFFER_ID)

  useEffect(() => {
    AnalyticsService.sendUniquePageview(ROUTE_PATHS.SAMSCLUB)
    AnalyticsService.sendGA4FunnelEvent(PROMO_CUSTOM_LANDING_PAGE_VIEW, {
      page_path: ROUTE_PATHS.SAMSCLUB,
      partner_name: "SamsClub"
    });
    dispatch({ type: "RESETUSER" })
  }, [dispatch])

  const fetchConfigData = useCallback(async (isMounted) => {
    setIsLoading(true)
    try {
      // 1. Fetch Maintenance Mode Details
      const [
        clientMaintenanceFlagResult,
        clientMaintenanceText,
        pageNotFound,
        offerMaintenanceFlag,
        offerMaintenanceText
      ] = await Promise.all([
        OfferApi.getClientAsset("MaintenanceFlag"),
        OfferApi.getClientAsset("MaintenanceText"),
        OfferApi.getOfferAsset(offerId, "PAGE_NOT_FOUND"),
        OfferApi.getOfferAsset(offerId, "MaintenanceFlag"),
        OfferApi.getOfferAsset(offerId, "MaintenanceText")
      ])
      if (isMounted) {
        setIsPageNotFound(pageNotFound)
        if (clientMaintenanceFlagResult.notes?.toLowerCase() === "true") {
          setMaintenanceText(clientMaintenanceText.notes)
          setInMaintenanceMode(true)
        } else if (offerMaintenanceFlag.notes?.toLowerCase() === "true") {
          setMaintenanceText(offerMaintenanceText.notes)
          setInMaintenanceMode(true)
        } else {
          setInMaintenanceMode(false)
        }
      }
      // 2. Fetch Landing Page Text (only if not in maintenance mode)
      if (!inMaintenanceMode && isMounted) {
        const [
          mainImage,
          mainImageText,
          firstSectionText,
          secondSectionText
        ] = await Promise.all([
          OfferApi.getOfferAsset(offerId, "BANNER_IMAGE_LANDING_PAGE"),
          OfferApi.getOfferAsset(offerId, "IMAGE_TEXT_LANDING_PAGE"),
          OfferApi.getClientAsset("SAMS_OFFER_TEXT_1"),
          OfferApi.getClientAsset("SAMS_OFFER_TEXT_2")
        ])
        if (isMounted) {
          setLandingPageImage(mainImage.notes || "")
          setLandingPageImageText(mainImageText.notes || "")
          setLandingPageFirstSection(firstSectionText.notes || "")
          setLandingPageSecSection(secondSectionText.notes || "")
        }
      }
    } catch (error) {
      if (isMounted) {
        if (error.message === "Unable to retrieve client asset") {
          setIsErrorFetchingMaintenanceText(true)
        }
        setErrorMessage("Something went wrong. Please try again.")
      }
    } finally {
      if (isMounted) {
        setIsLoading(false)
      }
    }
  }, [offerId, inMaintenanceMode])

  useEffect(() => {
    let isMounted = true
    fetchConfigData(isMounted)
    return () => {
      isMounted = false
    }
  }, [fetchConfigData])

  useEffect(() => {
    const getOfferCampignForImage = async () => {
      setIsLoading(true)
      try {
        const resOfferCampaignDetails = await OfferApi.getOfferCampaignDetails(offerId)
        resOfferCampaignDetails.data.campaignId === 0 ? setOfferExpire("OFFER_EXPIRED") : setCampaignId(resOfferCampaignDetails.data.campaignId)
        setIsLoading(false)
      } catch (error) {
        if (error.message === "Unable to retrieve offer campaign details") {
          setOfferExpire("OFFER_EXPIRED")
        }
      } finally {
        setIsLoading(false)
      }
    }
    getOfferCampignForImage()
  }, [offerId])

  const ActiveContent = () => (
    <>
      {!isPageNotFound && <SamsClubGetStartedComponent errorMessage={state.commonError} offerId={offerId} firstSectionText={landingPageFirstSection} secondSectionText={landingPageSecSection} setErrorMessage={setErrorMessage} />}
      <CheckStatusComponent />
    </>
  )

  return (
    <>
      <SamsClubWelcomeComponent backgrndImgText={landingPageImageText}
        backgrndImg={landingPageImage} offerCampaignId={campaignId} offerCampaignIdExpire={offerEpire} />
      {isPageNotFound && <PageNotFound />}
      {isErrorFetchingMaintenanceText &&
        <div className="text-center container">
          <h1 className="magenta">Temporarily Unavailable</h1>
          <p>This site is currently under going scheduled maintenance. We&apos;ll be back soon.</p>
          <p> Thank you for your patience.</p>
        </div>}
      {isLoading && <div style={{ textAlign: "center" }}>Loading ...</div>}
      {errorMessage.length > 0 && !isLoading && !isErrorFetchingMaintenanceText && <div style={{ textAlign: "center" }}>{errorMessage}</div>}
      {inMaintenanceMode && !isLoading && <MaintenanceModeComponent maintenanceText={maintenanceText} />}
      {!inMaintenanceMode && !isLoading && !isErrorFetchingMaintenanceText && <ActiveContent />}
    </>
  )
}

SamsClub.propTypes = {
  history: PropTypes.object
}
